<template>
    <section class="foxy-panel overflow-hidden shadow-md" aria-labelledby="replies-heading">
        <header class="foxy-divider border-b px-4 py-4 sm:px-6 sm:py-5">
            <h2 id="replies-heading" class="text-lg font-bold text-foxy-text sm:text-xl">
                Відповіді · <span id="replies-count">{{ totalReplyCount }}</span>
            </h2>
        </header>

        <div class="px-4 py-5 sm:px-6 sm:py-6">
        <div ref="topLevelComposerAnchor" aria-hidden="true"></div>

        <div
            v-if="isAuthenticated && !isDiscussionClosed"
            ref="composer"
            class="w-full scroll-mt-24 rounded-xl border border-foxy-border bg-foxy-elevated/45 p-3 sm:p-4"
            :class="replyTarget ? 'my-3' : 'mb-6'"
            data-shared-reply-composer
            :data-reply-target-id="replyTarget ? replyTarget.id : null"
        >
            <form class="space-y-3" @submit.prevent="submitReply">
                <div
                    v-if="replyTarget"
                    class="flex min-w-0 items-center justify-between gap-3 text-sm text-foxy-text-secondary"
                >
                    <span class="min-w-0 truncate">Відповідь для <span class="font-medium text-foxy-text">{{ replyTarget.label }}</span></span>
                    <button
                        type="button"
                        class="min-h-11 shrink-0 rounded-lg px-3 font-medium text-foxy-action hover:bg-foxy-action/10"
                        aria-label="Скасувати відповідь на повідомлення"
                        @click="clearReplyTarget()"
                    >
                        Скасувати
                    </button>
                </div>

                <div class="relative">
                    <textarea
                        ref="composerTextarea"
                        v-model="composerContent"
                        rows="3"
                        maxlength="1000"
                        placeholder="Що ви думаєте про це? (використовуйте @ для додаткової згадки)"
                        class="foxy-textarea !resize-none !px-4 !py-4"
                        required
                        @input="handleMentionInput"
                        @keydown="handleMentionKeydown"
                    ></textarea>

                    <div
                        v-if="showMentionDropdown && mentionUsers.length"
                        class="foxy-panel absolute left-0 top-full z-[9999] mt-1 max-h-60 min-w-[250px] max-w-[min(300px,100%)] overflow-y-auto !rounded-xl shadow-xl"
                    >
                        <button
                            v-for="(user, index) in mentionUsers"
                            :key="user.id"
                            type="button"
                            class="flex w-full cursor-pointer items-center gap-3 px-4 py-3 text-left transition-colors"
                            :class="selectedMentionIndex === index ? 'bg-foxy-action/10' : 'hover:bg-foxy-elevated'"
                            @mouseenter="selectedMentionIndex = index"
                            @click="insertMention(user.username)"
                        >
                            <user-avatar :src="user.avatar_display || ''" :name="user.name" size="sm" />
                            <span class="min-w-0">
                                <span class="block truncate text-sm font-medium text-foxy-text">{{ user.name }}</span>
                                <span class="block truncate text-xs text-foxy-text-secondary">@{{ user.username }}</span>
                            </span>
                        </button>
                    </div>
                </div>

                <div class="flex justify-end">
                    <button
                        type="submit"
                        class="foxy-btn-primary min-h-11 !px-6 !py-3 !text-base !font-medium disabled:cursor-not-allowed disabled:opacity-60"
                        :disabled="isSubmitting || !composerContent.trim()"
                    >
                        {{ isSubmitting ? 'Відправляємо…' : 'Відправити' }}
                    </button>
                </div>
            </form>
        </div>

        <div v-else-if="!isAuthenticated && !isDiscussionClosed" class="mb-6">
            <div class="rounded-xl border border-foxy-border bg-foxy-elevated/45 p-5 text-center sm:p-6">
                <h3 class="mb-2 text-xl font-bold text-foxy-text">Увійдіть, щоб відповісти</h3>
                <p class="foxy-muted mb-6">Відповіді доступні всім. Авторизуйтеся, щоб долучитися до дискусії.</p>
                <div class="flex flex-col justify-center gap-3 sm:flex-row">
                    <a href="/login" class="foxy-btn-primary !px-6 !py-3 !text-base !font-semibold">Увійти</a>
                    <a href="/register" class="foxy-btn-outline !border-2 !px-6 !py-3 !text-base !font-semibold">Зареєструватися</a>
                </div>
            </div>
        </div>

        <div v-if="isDiscussionClosed" class="mb-6 flex items-center gap-3 rounded-xl border border-foxy-border bg-foxy-elevated/55 p-4 text-sm text-foxy-text-secondary">
            <i class="fas fa-lock shrink-0" aria-hidden="true"></i>
            <p>Дискусію закрито для нових відповідей.</p>
        </div>

        <div id="replies-section">
            <div v-if="localThreads.length" id="replies-container" class="space-y-6">
                <section
                    v-for="thread in localThreads"
                    :key="thread.root.id"
                    class="discussion-branch"
                    :data-root-reply-id="thread.root.id"
                >
                    <discussion-reply
                        :reply="thread.root"
                        :discussion-slug="discussionSlug"
                        :highlighted-reply-id="activeHighlightedReplyId"
                        @reply-requested="selectReplyTarget"
                        @focus-reply="navigateToReply"
                        @threads-replaced="replaceThreads"
                        @like-toggled="handleLikeToggled"
                        @show-notification="showNotification"
                    />

                    <div v-if="thread.replies.length" class="mt-1 space-y-1">
                        <discussion-reply
                            v-for="reply in thread.replies"
                            :key="reply.id"
                            :reply="reply"
                            :is-descendant="true"
                            :discussion-slug="discussionSlug"
                            :highlighted-reply-id="activeHighlightedReplyId"
                            @reply-requested="selectReplyTarget"
                            @focus-reply="navigateToReply"
                            @threads-replaced="replaceThreads"
                            @like-toggled="handleLikeToggled"
                            @show-notification="showNotification"
                        />
                    </div>
                </section>
            </div>

            <div v-else id="no-replies-message" class="py-12 text-center">
                <i class="far fa-comments mb-4 text-4xl text-foxy-text-secondary/70"></i>
                <h3 class="mb-2 text-xl font-semibold text-foxy-text-secondary">Поки що немає відповідей</h3>
                <p class="foxy-muted">Станьте першим, хто відповість у цій дискусії!</p>
            </div>
        </div>
        </div>
    </section>
</template>

<script>
import axios from 'axios';
import DiscussionReply from './DiscussionReply.vue';
import UserAvatar from './UserAvatar.vue';
import { formatCount } from '../utils/ukrainianPluralization';

export default {
    name: 'DiscussionRepliesList',
    components: { DiscussionReply, UserAvatar },
    props: {
        threads: { type: [Array, Object], default: () => [] },
        discussionSlug: { type: String, required: true },
        currentUserId: { type: Number, default: null },
        isDiscussionClosed: { type: Boolean, default: false },
    },
    data() {
        return {
            localThreads: Array.isArray(this.threads) ? [...this.threads] : Object.values(this.threads || {}),
            replyTarget: null,
            composerContent: '',
            isSubmitting: false,
            activeHighlightedReplyId: null,
            targetHighlightTimeout: null,
            mentionUsers: [],
            showMentionDropdown: false,
            selectedMentionIndex: -1,
            mentionSearchTimeout: null,
        };
    },
    computed: {
        isAuthenticated() {
            return this.currentUserId !== null && this.currentUserId !== undefined && this.currentUserId !== '';
        },
        totalReplyCount() {
            return this.localThreads.reduce((count, thread) => (
                count
                    + (thread.root && !thread.root.is_tombstone ? 1 : 0)
                    + (thread.replies || []).filter((reply) => !reply.is_tombstone).length
            ), 0);
        },
    },
    mounted() {
        window.addEventListener('hashchange', this.handleHashAnchor);
        this.$nextTick(this.handleHashAnchor);
    },
    beforeDestroy() {
        this.restoreComposerToTop();
        this.cleanupListeners();
    },
    methods: {
        cleanupListeners() {
            window.removeEventListener('hashchange', this.handleHashAnchor);
            if (this.targetHighlightTimeout) window.clearTimeout(this.targetHighlightTimeout);
            if (this.mentionSearchTimeout) window.clearTimeout(this.mentionSearchTimeout);
        },
        allReplies() {
            return this.localThreads.flatMap((thread) => [thread.root, ...(thread.replies || [])]);
        },
        findReply(replyId) {
            return this.allReplies().find((reply) => Number(reply.id) === Number(replyId)) || null;
        },
        restoreComposerToTop() {
            const anchor = this.$refs.topLevelComposerAnchor;
            const composer = this.$refs.composer;
            if (!anchor || !composer) return false;
            anchor.insertAdjacentElement('afterend', composer);
            return true;
        },
        positionComposer() {
            if (!this.replyTarget) return this.restoreComposerToTop();

            const reply = this.findReply(this.replyTarget.id);
            const target = this.$el.querySelector(`[data-reply-id="${Number(this.replyTarget.id)}"]`);
            const composer = this.$refs.composer;
            if (!reply || reply.is_tombstone || !reply.capabilities?.can_reply || !target || !composer) {
                this.replyTarget = null;
                this.restoreComposerToTop();
                return false;
            }

            target.insertAdjacentElement('afterend', composer);
            return true;
        },
        selectReplyTarget(reply) {
            if (!reply?.capabilities?.can_reply || reply.is_tombstone) return;
            this.replyTarget = {
                id: Number(reply.id),
                username: reply.author?.username || null,
                label: reply.author?.username ? `@${reply.author.username}` : 'видаленого користувача',
            };
            this.$nextTick(() => {
                if (this.positionComposer()) this.$refs.composerTextarea?.focus();
            });
        },
        clearReplyTarget(focusComposer = true) {
            this.replyTarget = null;
            this.$nextTick(() => {
                this.restoreComposerToTop();
                if (focusComposer) this.$refs.composerTextarea?.focus();
            });
        },
        async submitReply() {
            if (!this.composerContent.trim() || this.isSubmitting) return;
            if (this.replyTarget) {
                const target = this.findReply(this.replyTarget.id);
                if (!target || target.is_tombstone || !target.capabilities?.can_reply) {
                    this.clearReplyTarget(false);
                    this.showNotification('Ця відповідь більше недоступна. Чернетку збережено.', 'error');
                    return;
                }
            }
            const targetId = this.replyTarget ? Number(this.replyTarget.id) : null;
            const content = this.composerContent;
            this.hideMentionDropdown();
            this.isSubmitting = true;

            try {
                const { data } = await axios.post(`/discussions/${this.discussionSlug}/replies`, {
                    content,
                    parent_id: targetId,
                });
                if (data.success) {
                    this.restoreComposerToTop();
                    this.localThreads = data.threads || [];
                    this.composerContent = '';
                    this.replyTarget = null;
                    this.showNotification('Відповідь успішно додано!', 'success');
                    await this.$nextTick();
                    this.navigateToReply(data.reply_id);
                    this.updateDiscussionRepliesCount();
                }
            } catch (error) {
                const targetRejected = error.response?.status === 422
                    && Object.prototype.hasOwnProperty.call(error.response?.data?.errors || {}, 'parent_id');
                if (this.replyTarget && ([404, 409].includes(error.response?.status) || targetRejected)) {
                    this.clearReplyTarget(false);
                }
                this.showNotification(error.response?.data?.message || 'Не вдалося надіслати відповідь.', 'error');
            } finally {
                this.isSubmitting = false;
            }
        },
        replaceThreads(threads) {
            this.restoreComposerToTop();
            this.localThreads = Array.isArray(threads) ? threads : Object.values(threads || {});
            this.updateDiscussionRepliesCount();
            this.$nextTick(() => {
                this.positionComposer();
                this.handleHashAnchor();
            });
        },
        handleLikeToggled(payload) {
            const reply = this.findReply(payload.replyId);
            if (!reply) return;
            this.$set(reply, 'viewer_liked', payload.liked);
            this.$set(reply, 'likes_count', payload.likesCount);
        },
        handleHashAnchor() {
            const match = window.location.hash.match(/^#reply-(\d+)$/);
            if (match) this.focusReply(Number(match[1]));
        },
        navigateToReply(replyId) {
            if (!this.findReply(replyId)) return;
            const hash = `#reply-${replyId}`;
            if (window.location.hash !== hash) {
                window.history.pushState({}, '', hash);
            }
            this.focusReply(replyId);
        },
        async focusReply(replyId) {
            if (!this.findReply(replyId)) return false;
            this.activeHighlightedReplyId = Number(replyId);
            await this.$nextTick();
            const target = this.$el.querySelector(`[data-reply-id="${Number(replyId)}"]`);
            if (!target) return false;

            this.$el.querySelectorAll('[aria-current="true"]').forEach((element) => element.removeAttribute('aria-current'));
            target.setAttribute('aria-current', 'true');
            const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
            target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
            target.focus({ preventScroll: true });

            if (this.targetHighlightTimeout) window.clearTimeout(this.targetHighlightTimeout);
            this.targetHighlightTimeout = window.setTimeout(() => {
                if (Number(this.activeHighlightedReplyId) === Number(replyId)) {
                    this.activeHighlightedReplyId = null;
                }
                target.removeAttribute('aria-current');
            }, 5000);
            return true;
        },
        handleMentionInput(event) {
            const textarea = event.target;
            const cursor = textarea.selectionStart;
            const beforeCursor = (textarea.value || this.composerContent).substring(0, cursor);
            const atIndex = beforeCursor.lastIndexOf('@');
            if (atIndex === -1) return this.hideMentionDropdown();

            const query = beforeCursor.substring(atIndex + 1);
            if (query.includes(' ') || query.includes('\n')) return this.hideMentionDropdown();
            this.searchUsers(query.trim());
        },
        searchUsers(query) {
            if (this.mentionSearchTimeout) window.clearTimeout(this.mentionSearchTimeout);
            this.mentionSearchTimeout = window.setTimeout(async () => {
                try {
                    const response = await fetch(`/api/users/search${query ? `?q=${encodeURIComponent(query)}` : ''}`);
                    if (!response.ok) throw new Error(`HTTP ${response.status}`);
                    const data = await response.json();
                    this.mentionUsers = data.users || [];
                    this.showMentionDropdown = this.mentionUsers.length > 0;
                    this.selectedMentionIndex = -1;
                } catch (error) {
                    this.hideMentionDropdown();
                }
            }, 200);
        },
        insertMention(username) {
            const textarea = this.$refs.composerTextarea;
            if (!textarea) return;
            const cursor = textarea.selectionStart;
            const beforeCursor = this.composerContent.substring(0, cursor);
            const atIndex = beforeCursor.lastIndexOf('@');
            if (atIndex === -1) return;

            this.composerContent = `${this.composerContent.substring(0, atIndex)}@${username} ${this.composerContent.substring(cursor)}`;
            this.hideMentionDropdown();
            this.$nextTick(() => {
                const nextCursor = atIndex + username.length + 2;
                textarea.setSelectionRange(nextCursor, nextCursor);
                textarea.focus();
            });
        },
        handleMentionKeydown(event) {
            if (!this.showMentionDropdown || !this.mentionUsers.length) return;
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                this.selectedMentionIndex = Math.min(this.selectedMentionIndex + 1, this.mentionUsers.length - 1);
            } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                this.selectedMentionIndex = Math.max(this.selectedMentionIndex - 1, 0);
            } else if ((event.key === 'Enter' || event.key === 'Tab') && this.selectedMentionIndex >= 0) {
                event.preventDefault();
                this.insertMention(this.mentionUsers[this.selectedMentionIndex].username);
            } else if (event.key === 'Escape') {
                this.hideMentionDropdown();
            }
        },
        hideMentionDropdown() {
            this.showMentionDropdown = false;
            this.mentionUsers = [];
            this.selectedMentionIndex = -1;
            if (this.mentionSearchTimeout) {
                window.clearTimeout(this.mentionSearchTimeout);
                this.mentionSearchTimeout = null;
            }
        },
        updateDiscussionRepliesCount() {
            const count = this.totalReplyCount;
            const sectionCount = document.querySelector('#replies-count');
            if (sectionCount) sectionCount.textContent = count;
            const statsIcon = document.querySelector('.flex.items-center.space-x-2 .fas.fa-comments');
            const statsCount = statsIcon?.parentElement?.querySelector('span');
            if (statsCount) statsCount.textContent = formatCount(count, 'відповідь', 'відповіді', 'відповідей');
        },
        showNotification(message, type = 'info') {
            const notification = document.createElement('div');
            notification.className = `fixed right-4 top-4 z-50 rounded-lg border px-6 py-3 font-medium shadow-lg ${
                type === 'success'
                    ? 'border-foxy-success/30 bg-foxy-success/10 text-foxy-success'
                    : type === 'error'
                        ? 'border-foxy-error/30 bg-foxy-error/10 text-foxy-error'
                        : 'border-foxy-action/30 bg-foxy-action/10 text-foxy-action'
            }`;
            notification.textContent = message;
            document.body.appendChild(notification);
            window.setTimeout(() => notification.remove(), 3000);
        },
    },
};
</script>

<style scoped>
.discussion-branch + .discussion-branch {
    border-top: 1px solid rgb(var(--foxy-border) / 0.7);
    padding-top: 1.25rem;
}
</style>
